import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ActionDecorations,
	ActionFeedback,
	ActionInput,
	ActionMedia,
	ActionPrivacy,
	ActionSubmit,
	ActionTags,
	ActionText,
	ActionValidator,
	parseState,
	RichMediaState,
	useBaseModules,
} from '@bonusly/common-action-input';
import {
	Box,
	Checkbox,
	Circle,
	Flex,
	Hide,
	InformationCircleSolid,
	LockClosedOutline,
	ModalBody,
	ModalFooter,
	rem,
	Text,
	Tooltip,
} from '@bonusly/design-system';

import { RecipientSelectorWithCsvUpload } from '../../../../../../components/action-input/components/recipient/recipient-selector-with-csv-upload';
import { Reveal } from '../../../../../../components/display/reveal';
import { Award } from '../../../../../../types/bonus';
import { AwardHeader } from './AwardHeader';
import { useAwardContent } from './hooks/useAwardContent';
import { useAwardModal } from './hooks/useAwardModal';
import { useSharedAwardConfig } from './hooks/useSharedAwardConfig';
import { useTemplatePreview } from './hooks/useTemplatePreview';

type OldUneditableAwardInputProps = {
	award: Award;
	hideGiver: boolean;
	setHideGiver: (hideGiver: boolean) => void;
	setIsSubmitting: (isSubmitting: boolean) => void;
};

export const OldUneditableAwardInput: FC<OldUneditableAwardInputProps> = ({ award, hideGiver, setHideGiver, setIsSubmitting }) => {
	const { onClose } = useAwardModal();
	const { t } = useTranslation('recognition');

	const config = useSharedAwardConfig({ award, hideGiver, isTemplatedAward: true, onSuccess: onClose });
	const { amount } = parseState(award.reason);

	const modules = useBaseModules({
		includeGif: true,
		includeAttachment: true,
		includeRecipient: true,
		includePrivacy: true,
		includeVideoRecording: true,
	});

	const validator = useMemo(() => {
		return new ActionValidator({
			requireRecipient: true,
		});
	}, []);

	const calculateTotalFn = useCallback(
		(data: RichMediaState) => {
			return data.recipientCount * (amount || 0);
		},
		[amount]
	);

	const content = useAwardContent();

	useEffect(() => {
		setIsSubmitting(config.isSubmitting);
	}, [config.isSubmitting, setIsSubmitting]);

	return (
		<ActionInput
			autofocus="end"
			content={content}
			context="templated-award"
			giveAmount={amount}
			modules={modules}
			placeholder={t('awards.give_modal.receipientPlaceholder')}
			calculateTotalFn={calculateTotalFn}
			validator={validator}
			options={{
				recipient: {
					component: RecipientSelectorWithCsvUpload,
				},
			}}
			{...config}
		>
			<ModalBody py={4}>
				<AwardHeader award={award} mb={4} />

				<Flex alignItems="flex-start" justifyContent="space-between">
					<ActionTags />
					<Hide below="xs">
						<ActionPrivacy />
					</Hide>
				</Flex>

				<Box mb={2}>
					<ActionText inputStyles={{ minHeight: rem(75) }} />
					<Text as="span">
						<Box as="span" fontWeight="medium">
							{t('awards.give_modal.template_label')}:
						</Box>

						{useTemplatePreview(award)}

						<Box as="span" display="inline-block" ml={1}>
							<Tooltip label={t('awards.give_modal.template_tooltip_custom_message')}>
								<Circle bg="blackAlpha.50" size={rem(32)}>
									<LockClosedOutline size="sm" />
								</Circle>
							</Tooltip>
						</Box>
						<Box mt={2}>
							<Checkbox isChecked={!hideGiver} onChange={(event) => setHideGiver(!event.target.checked)}>
								<Text mt={1}>{t('awards.give_modal.show_giver_label')}</Text>
							</Checkbox>
						</Box>
					</Text>
					<ActionFeedback />
				</Box>

				<ActionMedia w="full" />
			</ModalBody>

			<ModalFooter borderTop="1px solid" borderColor="gray.100" flexDirection="column" alignItems="initial">
				<Reveal if={award?.approval_process}>
					<Flex bg="gray.25" borderRadius="md" direction="row" px={3} py={2} mb={4}>
						<InformationCircleSolid color="gray.500" />

						<Box ml={2}>
							<Text fontWeight="semibold" color="gray.900" fontSize="sm">
								{t('recognition:awards.give_modal.requires_approval_title')}
							</Text>
							<Text fontWeight="normal" color="gray.900" fontSize="sm">
								{t('recognition:awards.give_modal.requires_approval_description')}
							</Text>
						</Box>
					</Flex>
				</Reveal>

				<Flex
					alignItems={{ base: 'stretch', xs: 'flex-start' }}
					direction={{ base: 'column', xs: 'row' }}
					gap={2}
					justifyContent="space-between"
					mb={2}
					w="full"
				>
					<Flex justifyContent="space-between" flexGrow={1}>
						<ActionDecorations />
						<Hide above="xs">
							<ActionPrivacy />
						</Hide>
					</Flex>

					<ActionSubmit size="lg" />
				</Flex>
			</ModalFooter>
		</ActionInput>
	);
};
